import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@finn-auto/design-system/atoms/modal';
import type {
  SlotHold,
  TrackingMap,
  StageRef,
} from '../../types/taActionBoard';
import { EditPanelBody } from './EditPanelBody';
import { AskBody, HoldsBody } from './SchedulingModalBodies';
import { NudgeBody } from './NudgeModalBody';
import { ArchiveBody, MoveBody } from './FeedbackModalBodies';
import { ArchivePending } from './ArchivePending';
import { ModalActions } from './ModalActions';
export type { ModalHandlers, ModalState } from './actionModalTypes';
import type { ModalHandlers, ModalState } from './actionModalTypes';
import { ConfirmCaseBody } from './ConfirmCaseBody';
import { fmtSlotRange } from './format';
import { OfferedBody } from './OfferedModalBody';


interface ActionModalsProps {
  modal: ModalState;
  archiveReasons: string[];
  /** ALL Lever stages in pipeline order — target list for the Move modal */
  stages: StageRef[];
  /** First name of the signed-in recruiter — signs the drafted invite email */
  senderFirstName: string;
  /** The board being viewed (the recruiter's own or a colleague's) — the Move
   *  modal drafts the invite email as this sender and mints the booking invite. */
  boardEmail: string;
  /** A Google account is connected on the board — the Move modal may offer to send. */
  gmailConnected: boolean;
  /** postingId → calendar holds; the confirm modal books the offered one */
  holds?: Record<string, SlotHold[]>;
  /** opportunityId → tracking; carries times proposed WITHOUT a hold behind them */
  tracking?: TrackingMap;
  onClose: () => void;
  handlers: ModalHandlers;
}

/**
 * All board actions go through a design-system Modal confirmation. Confirming
 * closes the modal immediately — the caller applies the optimistic update and
 * runs the API call in the background (revert + toast on failure).
 */
export function ActionModals({
  modal,
  archiveReasons,
  stages,
  senderFirstName,
  boardEmail,
  gmailConnected,
  onClose,
  handlers,
  holds,
  tracking,
}: ActionModalsProps) {
  if (!modal) return null;
  // The earlier "blank white page" on opening these modals was a dev-only crash
  // from a duplicated @radix-ui/react-dialog module — fixed in vite.config.ts
  // (optimizeDeps.include). No variant="small" here on purpose: the small
  // variant becomes a mobile bottom-drawer on viewports ≤ the DS md breakpoint
  // (1120px — most laptops), and the default large dialog gives the email draft
  // room. Both modals open as overlays in the same tab.
  //
  // ModalContent className: below the md breakpoint the DS dialog is a
  // fullscreen `size-full` sheet, which left a huge empty band between the
  // fields and the footer on laptops. The `!` overrides keep it a natural-
  // height centered card at every viewport (tailwind-merge does NOT drop
  // `size-full` for a later `h-*`/`w-*`, so plain overrides would not win).
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,52rem)] gap-6 overflow-auto rounded p-6 md:p-14">
        <ActionModalBody
          holds={holds}
          tracking={tracking}
          modal={modal}
          archiveReasons={archiveReasons}
          stages={stages}
          senderFirstName={senderFirstName}
          boardEmail={boardEmail}
          gmailConnected={gmailConnected}
          onClose={onClose}
          handlers={handlers}
        />
      </ModalContent>
    </Modal>
  );
}

/**
 * NOTE: deliberately NOT named "Modal…" — the design-system Modal/ModalContent
 * clone any direct child whose component name starts with "Modal" to inject
 * drawer/variant props, which must not happen to this plain switch component.
 */
function ActionModalBody({
  holds,
  tracking,
  modal,
  archiveReasons,
  stages,
  senderFirstName,
  boardEmail,
  gmailConnected,
  onClose,
  handlers,
}: {
  holds?: Record<string, SlotHold[]>;
  tracking?: TrackingMap;
  modal: NonNullable<ModalState>;
  archiveReasons: string[];
  stages: StageRef[];
  senderFirstName: string;
  boardEmail: string;
  /** A Google account is connected on the board — the Move modal may offer to send. */
  gmailConnected: boolean;
  onClose: () => void;
  handlers: ModalHandlers;
}) {
  switch (modal.kind) {
    case 'archive':
      if (modal.item.modelPending) return <ArchivePending item={modal.item} onCancel={onClose} />;
      return (
        <ArchiveBody
          key={modal.item.opportunityId}
          item={modal.item}
          reasons={archiveReasons}
          onConfirm={(reason, noteText, scheduleAt) => {
            handlers.onArchive(modal.role, modal.item, reason, noteText, scheduleAt);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'move':
      return (
        <MoveBody
          key={modal.item.opportunityId}
          role={modal.role}
          item={modal.item}
          stages={stages}
          senderFirstName={senderFirstName}
          boardEmail={boardEmail}
          gmailConnected={gmailConnected}
          onConfirm={(targetStage, email) => {
            handlers.onMove(modal.role, modal.item, targetStage, email);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'holds':
      return (
        <HoldsBody
          role={modal.role}
          slots={modal.slots}
          onConfirm={() => {
            handlers.onHolds(modal.role, modal.slots);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'offered':
      return (
        <OfferedBody
          key={modal.candidate.opportunityId}
          role={modal.role}
          candidate={modal.candidate}
          senderFirstName={senderFirstName}
          gmailConnected={gmailConnected}
          preselected={modal.preselected}
          onConfirm={(slotLabels, channel, email, starts, slotMeta) => {
            handlers.onOffered(modal.candidate, slotLabels, channel, modal.role, email, starts, slotMeta);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'confirmed':
      return (
        <>
          <ModalHeader>
            <ModalTitle>Confirm the case — {modal.candidate.name}</ModalTitle>
          </ModalHeader>
          <ConfirmCaseBody
            candidate={modal.candidate}
            panel={modal.role.scheduling.panel}
            holds={holds?.[modal.role.postingId] ?? []}
            tracking={tracking?.[modal.candidate.opportunityId]}
            onConfirm={(start, opts) => {
              handlers.onConfirmed(modal.candidate, modal.role, start, opts);
              onClose();
            }}
            onCancel={onClose}
          />
        </>
      );
    case 'withdraw':
      return (
        <ConfirmBody
          title={`Withdraw proposed times — ${modal.candidate.name}`}
          body="Withdraw the proposed slot? The candidate goes back to “ready to schedule” and their slots read free to propose again. The calendar holds STAY — a declined slot is the likeliest one to re-propose, so release it deliberately under “Holds” when you no longer want it."
          confirmLabel="Withdraw proposed times"
          onConfirm={() => {
            handlers.onWithdraw(modal.candidate, modal.role);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'release-hold':
      return (
        <ConfirmBody
          title="Release this calendar hold?"
          body={`${fmtSlotRange(modal.hold)} — the hold is deleted from the panel’s calendars and the slot is free for anything else.${
            modal.hold.offeredTo && modal.hold.state === 'offered'
              ? ` It is currently PROPOSED to ${modal.hold.offeredTo.candidateName || 'a candidate'}, who may still accept it.`
              : ''
          } This cannot be undone — holding it again creates a new hold, if the time is still free.`}
          confirmLabel="Release the hold"
          onConfirm={() => {
            handlers.onReleaseHold(modal.role, modal.hold);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'release-legacy-holds':
      return (
        <ConfirmBody
          title="Release the holds from the earlier version?"
          body="These holds were created before holds were recorded per slot, so they are not linked to any candidate and cannot be managed individually. Releasing deletes all of them from the panel’s calendars."
          confirmLabel="Release them"
          onConfirm={() => {
            handlers.onReleaseLegacyHolds(modal.role);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'ask':
      return (
        <AskBody
          role={modal.role}
          slots={modal.slots}
          onConfirm={(destination, message, messages, language) => {
            handlers.onAsk(modal.role, destination, modal.slots, message, messages, language);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'nudge':
      return (
        <NudgeBody
          key={modal.item.opportunityId}
          role={modal.role}
          item={modal.item}
          onConfirm={(text, language) => {
            handlers.onNudgeFeedback(modal.role, modal.item, text, language);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'edit-panel':
      return (
        <EditPanelBody
          role={modal.role}
          suggestion={modal.suggestion ?? null}
          onConfirm={(panel) => {
            handlers.onSavePanel(modal.role, panel);
            onClose();
          }}
          onCancel={onClose}
        />
      );
  }
}

// ───────────────────────────── BODIES ─────────────────────────────

function ConfirmBody({
  title,
  body,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <p className="whitespace-pre-line text-sm text-gray-600">{body}</p>
      <ModalActions
        confirmLabel={confirmLabel}
        confirmDisabled={confirmDisabled}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
}
